import { apiRequest } from "./client";

// ---------- Общие типы данных статистики ----------

export interface PaymentBreakdownItem {
    payment_type: string;
    // "_count" — билетов (штук), "_orders_count" — уникальных заказов с этим способом оплаты.
    // Числа могут не совпадать, т.к. один заказ может содержать несколько билетов.
    paid_count: number;
    paid_orders_count: number;
    paid_sum: number;
    unpaid_count: number;
    unpaid_orders_count: number;
    unpaid_sum: number;
}

export interface StatsRow {
    id: string;
    level: "tour" | "city" | "distributor" | "api_distributor" | "total";
    name: string;
    start_at?: string | null;
    email?: string;
    seats_total: number;
    seats_free: number;
    seats_admin_reserved: number;
    tickets_sold: number;
    orders_count: number;
    orders_sum: number;
    paid_orders_count: number;
    paid_tickets_count: number;
    paid_sum: number;
    unpaid_orders_count: number;
    unpaid_tickets_count: number;
    unpaid_sum: number;
    regular_orders_count: number;
    agent_orders_count: number;
    invite_tickets_count: number;
    admin_given_tickets_count: number;
    payment_breakdown: PaymentBreakdownItem[];
}

export interface TourStatsRow extends StatsRow {
    level: "tour";
    cities: StatsRow[];
}

export interface ToursStatisticsResponse {
    tours: TourStatsRow[];
    total: StatsRow;
    ticket_count: number;
}

export interface DistributorTourRow extends StatsRow {
    level: "tour";
    cities: StatsRow[];
}

export interface DistributorStatsRow extends StatsRow {
    level: "distributor";
    email: string;
    tours: DistributorTourRow[];
}

export interface DistributorsStatisticsResponse {
    distributors: DistributorStatsRow[];
    total: StatsRow;
    ticket_count: number;
}

export interface ApiDistributorTourRow extends StatsRow {
    level: "tour";
    cities: StatsRow[];
}

export interface ApiDistributorStatsRow extends StatsRow {
    level: "api_distributor";
    email: string;
    tours: ApiDistributorTourRow[];
}

export interface ApiDistributorsStatisticsResponse {
    api_distributors: ApiDistributorStatsRow[];
    total: StatsRow;
    ticket_count: number;
}

// ---------- Общая статистика (сводка по периодам) ----------

export type GeneralStatsPeriod = "day" | "week" | "month" | "year";
// "abendkasse" — заказы специального пользователя вечерней кассы (отдельно от клиентов).
export type GeneralStatsSegment = "clients" | "distributors" | "api" | "abendkasse";

export interface GeneralStatsPeriodRow {
    key: string;
    period: GeneralStatsPeriod;
    period_start: string;
    period_end: string;
    label: string;
    orders_count: number;
    seats_count: number;
    sum_amount: number;
}

export interface GeneralStatisticsResponse {
    periods: GeneralStatsPeriodRow[];
    total_count: number;
    has_more: boolean;
}

export interface GeneralStatsCityRow {
    id: string;
    name: string;
    start_at: string | null;
    tickets_sold: number;
    orders_count: number;
    sum_amount: number;
}

export interface GeneralStatsEventRow {
    id: string;
    name: string;
    tickets_sold: number;
    orders_count: number;
    sum_amount: number;
    cities: GeneralStatsCityRow[];
}

export interface GeneralStatisticsEventsResponse {
    events: GeneralStatsEventRow[];
}

export interface GeneralStatisticsFilters {
    period: GeneralStatsPeriod;
    segment: GeneralStatsSegment;
    limit?: number;
    offset?: number;
    dateFrom?: string;
    dateTo?: string;
}

const buildGeneralParams = (filters: GeneralStatisticsFilters): string => {
    const params = new URLSearchParams();
    params.set("period", filters.period);
    params.set("segment", filters.segment);
    if (filters.limit != null) params.set("limit", String(filters.limit));
    if (filters.offset != null) params.set("offset", String(filters.offset));
    if (filters.dateFrom) params.set("date_from", filters.dateFrom);
    if (filters.dateTo) params.set("date_to", filters.dateTo);
    return params.toString();
};

export const getGeneralStatisticsRequest = (
    filters: GeneralStatisticsFilters
): Promise<GeneralStatisticsResponse> =>
    apiRequest<GeneralStatisticsResponse>(`/statistics/general?${buildGeneralParams(filters)}`, {
        method: "GET",
    });

export const getGeneralStatisticsEventsRequest = (
    period: GeneralStatsPeriod,
    periodStart: string,
    segment: GeneralStatsSegment
): Promise<GeneralStatisticsEventsResponse> => {
    const params = new URLSearchParams();
    params.set("period", period);
    params.set("period_start", periodStart);
    params.set("segment", segment);
    return apiRequest<GeneralStatisticsEventsResponse>(`/statistics/general/events?${params.toString()}`, {
        method: "GET",
    });
};

// ---------- Данные для фильтров ----------

export interface StatsFilterCity {
    id: string;
    name: string;
    start_at: string | null;
}

export interface StatsFilterTour {
    id: string;
    name: string;
    cities: StatsFilterCity[];
}

export interface StatsFilterDistributor {
    id: string;
    name: string;
    email: string;
}

export interface StatsFilterApiDistributor {
    id: string;
    name: string;
    email: string;
}

export interface StatisticsFiltersResponse {
    tours: StatsFilterTour[];
    distributors: StatsFilterDistributor[];
    api_distributors: StatsFilterApiDistributor[];
}

// ---------- Параметры запросов ----------

export interface TourStatisticsFilters {
    tourId?: string;
    tourCityId?: string;
    dateFrom?: string;
    dateTo?: string;
}

export interface DistributorStatisticsFilters extends TourStatisticsFilters {
    distributorId?: string;
}

export interface ApiDistributorStatisticsFilters extends TourStatisticsFilters {
    apiDistributorId?: string;
}

const buildParams = (
    filters: DistributorStatisticsFilters & ApiDistributorStatisticsFilters
): string => {
    const params = new URLSearchParams();
    if (filters.tourId) params.set("tour_id", filters.tourId);
    if (filters.tourCityId) params.set("tour_city_id", filters.tourCityId);
    if (filters.dateFrom) params.set("date_from", filters.dateFrom);
    if (filters.dateTo) params.set("date_to", filters.dateTo);
    if (filters.distributorId) params.set("distributor_id", filters.distributorId);
    if (filters.apiDistributorId) params.set("api_distributor_id", filters.apiDistributorId);
    return params.toString();
};

export const getStatisticsFiltersRequest = (): Promise<StatisticsFiltersResponse> =>
    apiRequest<StatisticsFiltersResponse>("/statistics/filters", { method: "GET" });

export const getTourStatisticsRequest = (
    filters: TourStatisticsFilters
): Promise<ToursStatisticsResponse> =>
    apiRequest<ToursStatisticsResponse>(`/statistics/tours?${buildParams(filters)}`, {
        method: "GET",
    });

export const getDistributorStatisticsRequest = (
    filters: DistributorStatisticsFilters
): Promise<DistributorsStatisticsResponse> =>
    apiRequest<DistributorsStatisticsResponse>(`/statistics/distributors?${buildParams(filters)}`, {
        method: "GET",
    });

export const getApiDistributorStatisticsRequest = (
    filters: ApiDistributorStatisticsFilters
): Promise<ApiDistributorsStatisticsResponse> =>
    apiRequest<ApiDistributorsStatisticsResponse>(`/statistics/api-distributors?${buildParams(filters)}`, {
        method: "GET",
    });

// ---------- Экспорт в Excel (либо файл сразу, либо фоновая генерация + письмо) ----------

export interface ExportResult {
    background: boolean;
    message?: string;
    blob?: Blob;
    filename?: string;
}

const downloadStatsExport = async (
    path: string,
    filters: DistributorStatisticsFilters & ApiDistributorStatisticsFilters,
    fallbackName: string
): Promise<ExportResult> => {
    const { API_BASE_URL } = await import("../config/env");
    const { getAccessToken } = await import("../storage/token.storage");

    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}?${buildParams(filters)}`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
            const data = await response.json();
            if (data?.detail) detail = String(data.detail);
        } catch {
            // ignore
        }
        throw new Error(detail);
    }

    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
        const data = await response.json();
        return { background: true, message: data?.message };
    }

    const blob = await response.blob();

    let filename = `${fallbackName}.xlsx`;
    const cd = response.headers.get("content-disposition");
    if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
    }

    return { background: false, blob, filename };
};

export const exportTourStatisticsRequest = (filters: TourStatisticsFilters): Promise<ExportResult> =>
    downloadStatsExport("/statistics/tours/export", filters, "statistics_tours");

export const exportDistributorStatisticsRequest = (
    filters: DistributorStatisticsFilters
): Promise<ExportResult> =>
    downloadStatsExport("/statistics/distributors/export", filters, "statistics_distributors");

export const exportApiDistributorStatisticsRequest = (
    filters: ApiDistributorStatisticsFilters
): Promise<ExportResult> =>
    downloadStatsExport("/statistics/api-distributors/export", filters, "statistics_api_distributors");

export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ---------------------------------------------------------------------------------
// «По типу покупателя»: клиенты / распространители / API / ручные распространители
// ---------------------------------------------------------------------------------

export type BuyerTypeKey = "clients" | "distributors" | "api" | "manual";

// Метрики первых трёх типов (клиенты, распространители, API).
export interface BuyerTypeMetrics {
    orders_count: number;
    tickets_sold: number;
    sum_amount: number;
    paid_tickets_count: number;
    paid_sum: number;
    unpaid_tickets_count: number;
    unpaid_sum: number;
}

// Метрики ручных распространителей: выдано / продано (отчитался) / не продано.
export interface ManualDistributorMetrics {
    orders_count: number;
    issued_tickets: number;
    issued_sum: number;
    tickets_sold: number;
    sum_amount: number;
    unsold_tickets: number;
    unsold_sum: number;
    // Деньги уже получены нами (заказ оплачен).
    paid_sum: number;
}

export type BuyerTypeCity<M> = M & { id: string; name: string; start_at: string | null };
export type BuyerTypeTour<M> = M & { id: string; name: string; cities: BuyerTypeCity<M>[] };

// Распространитель (обычный или API) в 2-м и 3-м списке: распространитель -> туры -> города.
export type BuyerTypeDistributorRow = BuyerTypeMetrics & {
    id: string;
    name: string;
    email: string | null;
    tours: BuyerTypeTour<BuyerTypeMetrics>[];
};

export type BuyerTypeRegularSegment = BuyerTypeMetrics & {
    key: "clients" | "distributors" | "api";
    label: string;
    tours: BuyerTypeTour<BuyerTypeMetrics>[];
    // Есть у «Распространители» и «Распространители по API».
    distributors?: BuyerTypeDistributorRow[];
};

export type ManualDistributorRow = ManualDistributorMetrics & {
    id: string;
    name: string;
    email: string | null;
    tours: BuyerTypeTour<ManualDistributorMetrics>[];
};

export type BuyerTypeManualSegment = ManualDistributorMetrics & {
    key: "manual";
    label: string;
    distributors: ManualDistributorRow[];
};

export interface BuyerTypesSummary {
    tickets_sold: number;
    sum_amount: number;
    orders_count: number;
    paid_sum: number;
    unpaid_sum: number;
    average_ticket_price: number;
    manual_issued_tickets: number;
    manual_sold_tickets: number;
    manual_sold_sum: number;
    manual_unsold_tickets: number;
}

export interface BuyerTypesStatisticsResponse {
    summary: BuyerTypesSummary;
    segments: (BuyerTypeRegularSegment | BuyerTypeManualSegment)[];
}

export const getBuyerTypesStatisticsRequest = (): Promise<BuyerTypesStatisticsResponse> =>
    apiRequest<BuyerTypesStatisticsResponse>("/statistics/buyer-types", { method: "GET" });

export interface ManualDistributorEventCity {
    id: string;
    name: string;
    start_at: string | null;
    issued_tickets: number;
    sold_tickets: number;
}

export interface ManualDistributorEvent {
    id: string;
    name: string;
    cities: ManualDistributorEventCity[];
}

export const getManualDistributorEventsRequest = (
    userId: string
): Promise<{ events: ManualDistributorEvent[] }> =>
    apiRequest<{ events: ManualDistributorEvent[] }>(`/statistics/manual-distributors/${userId}/events`, {
        method: "GET",
    });

export interface ManualDistributorSeat {
    order_seat_id: string;
    order_id: string;
    order_number: string;
    label: string;
    sector: string | null;
    row: string | null;
    seat: string | null;
    // Цена из прайса.
    list_price: number;
    // Сумма за место с учётом скидки заказа (как в статистике).
    price: number;
    distributor_paid: boolean;
    distributor_paid_at: string | null;
    order_is_paid: boolean;
}

export const getManualDistributorSeatsRequest = (
    userId: string,
    tourCityId: string
): Promise<{ seats: ManualDistributorSeat[] }> =>
    apiRequest<{ seats: ManualDistributorSeat[] }>(
        `/statistics/manual-distributors/${userId}/seats?tour_city_id=${encodeURIComponent(tourCityId)}`,
        { method: "GET" }
    );

// Отметка «оплачено у распространителя». Заказ при этом НЕ становится оплаченным.
export const setManualDistributorSeatsPaidRequest = (
    userId: string,
    orderSeatIds: string[],
    paid: boolean
): Promise<{ updated: number }> =>
    apiRequest<{ updated: number }>(`/statistics/manual-distributors/${userId}/seats/paid`, {
        method: "POST",
        body: JSON.stringify({ order_seat_ids: orderSeatIds, paid }),
        headers: { "Content-Type": "application/json" },
    });
